import { useEffect, useState } from "react";

import { cn } from "../../lib/utils";

// Peças visuais do telefone de WhatsApp, usadas pelo discador, pela tela de
// ligação e pela chamada recebida.

export const nomeDe = (l) => l?.contact?.name || formatarNumero(l?.number) || "Contato";

// "5561999998888" → "+55 (61) 99999-8888". Fora do Brasil, agrupa de 3 em 3
// depois do DDI — o bastante para ler sem contar dígito.
export const formatarNumero = (valor) => {
	const d = String(valor || "").replace(/\D/g, "");
	if (!d) return "";
	if (d.startsWith("55") && d.length >= 12) {
		const ddd = d.slice(2, 4);
		const resto = d.slice(4);
		const meio = resto.length > 8 ? resto.slice(0, resto.length - 4) : resto.slice(0, 4);
		const fim = resto.slice(meio.length);
		return `+55 (${ddd}) ${meio}${fim ? `-${fim}` : ""}`;
	}
	if (d.length <= 4) return d;
	return `+${d.slice(0, 2)} ${d.slice(2).replace(/(\d{3})(?=\d)/g, "$1 ")}`;
};

const CORES = [
	"from-emerald-400 to-teal-600",
	"from-sky-400 to-indigo-600",
	"from-amber-400 to-orange-600",
	"from-rose-400 to-pink-600",
	"from-violet-400 to-purple-600",
	"from-lime-400 to-green-600",
];

const iniciais = (nome) => {
	const partes = String(nome || "").replace(/[^\p{L}\p{N}\s]/gu, "").trim().split(/\s+/);
	if (!partes[0]) return "#";
	if (/^\d/.test(partes[0])) return partes[0].slice(-2);
	return (partes[0][0] + (partes[1]?.[0] || "")).toUpperCase();
};

// Tamanhos do avatar em classes fixas: o Tailwind só gera o que aparece
// escrito no código, então nada de montar o nome da classe na hora.
const TAMANHOS = {
	16: "size-4 text-[7px]",
	18: "size-[18px] text-[7px]",
	36: "size-9 text-xs",
	40: "size-10 text-sm",
	88: "size-22 text-3xl",
	96: "size-24 text-3xl",
};

// Avatar redondo: foto do contato quando existe; senão iniciais sobre um
// degradê escolhido pelo nome (a mesma pessoa tem sempre a mesma cor).
export const Avatar = ({ nome, foto, tamanho = 40, className }) => {
	const [falhou, setFalhou] = useState(false);
	const semente = [...String(nome || "")].reduce((a, c) => a + c.charCodeAt(0), 0);
	const cor = CORES[semente % CORES.length];
	return (
		<span
			className={cn(
				"relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full bg-gradient-to-br font-semibold text-white",
				TAMANHOS[tamanho] || TAMANHOS[40],
				cor,
				className
			)}
		>
			{foto && !falhou ? (
				<img
					src={foto}
					alt=""
					className="h-full w-full object-cover"
					onError={() => setFalhou(true)}
				/>
			) : (
				iniciais(nome)
			)}
		</span>
	);
};

export const Cronometro = ({ desde, className }) => {
	const [agora, setAgora] = useState(Date.now());
	useEffect(() => {
		const t = setInterval(() => setAgora(Date.now()), 1000);
		return () => clearInterval(t);
	}, []);
	if (!desde) return null;
	const s = Math.max(0, Math.round((agora - new Date(desde).getTime()) / 1000));
	const h = Math.floor(s / 3600);
	const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
	const ss = String(s % 60).padStart(2, "0");
	return <span className={cn("tabular-nums", className)}>{h ? `${h}:${m}:${ss}` : `${m}:${ss}`}</span>;
};

// Botão redondo com rótulo embaixo, como no telefone do celular.
export const BotaoRedondo = ({
	icone: Icone,
	rotulo,
	onClick,
	ativo = false,
	perigo = false,
	sucesso = false,
	tamanho = "md",
	disabled,
	carregando,
	title,
}) => (
	<div className="flex flex-col items-center gap-1.5">
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			title={title || rotulo}
			aria-label={title || rotulo}
			aria-pressed={ativo || undefined}
			className={cn(
				"inline-flex items-center justify-center rounded-full transition-all duration-150 active:scale-95 disabled:pointer-events-none disabled:opacity-40",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card",
				tamanho === "lg" ? "h-16 w-16" : "h-12 w-12",
				perigo
					? "bg-red-500 text-white shadow-lg shadow-red-500/30 hover:bg-red-600"
					: sucesso
						? "bg-emerald-500 text-white shadow-lg shadow-emerald-500/30 hover:bg-emerald-600"
						: ativo
							? "bg-foreground text-background"
							: "bg-muted text-foreground hover:bg-muted/70"
			)}
		>
			{carregando ? (
				<span className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
			) : (
				<Icone className={tamanho === "lg" ? "h-7 w-7" : "h-5 w-5"} />
			)}
		</button>
		{rotulo && <span className="text-[11px] text-muted-foreground">{rotulo}</span>}
	</div>
);

// Ondas saindo do avatar enquanto a ligação toca: três animate-ping lentos e
// defasados (duração e atraso por classe arbitrária do Tailwind).
export const Ondas = ({ className }) => (
	<>
		<span className={cn("absolute inset-0 animate-ping rounded-full bg-emerald-500/30 [animation-duration:2.2s] motion-reduce:hidden", className)} />
		<span className={cn("absolute inset-0 animate-ping rounded-full bg-emerald-500/30 [animation-delay:0.7s] [animation-duration:2.2s] motion-reduce:hidden", className)} />
		<span className={cn("absolute inset-0 animate-ping rounded-full bg-emerald-500/30 [animation-delay:1.4s] [animation-duration:2.2s] motion-reduce:hidden", className)} />
	</>
);
